import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, CheckCircle2, XCircle, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const Status = ({ m }) => {
  if (m.available === true) return <span className="inline-flex items-center gap-1 text-emerald-400 text-xs" data-testid="model-available"><CheckCircle2 size={12} /> available</span>;
  if (m.available === false) return <span className="inline-flex items-center gap-1 text-red-400 text-xs" data-testid="model-unavailable"><XCircle size={12} /> {m.failure_category || 'unavailable'}</span>;
  return <span className="inline-flex items-center gap-1 text-slate-500 text-xs" data-testid="model-not-probed"><HelpCircle size={12} /> not probed yet</span>;
};

const AdminAIModels = ({ initial }) => {
  const [data, setData] = useState(initial || null);
  const [telemetry, setTelemetry] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadTelemetry = () => axios.get(`${API}/api/ai/telemetry/summary`, { withCredentials: true }).then((r) => setTelemetry(r.data.models || [])).catch(() => setTelemetry([]));
  useEffect(() => { loadTelemetry(); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/ai/health/refresh`, {}, { withCredentials: true });
      setData((d) => ({ ...d, checked_at: r.data.checked_at, models: (d?.models || []).map((m) => ({ ...m, ...(r.data.health[m.id] || {}) })) }));
      toast.success('Model health re-probed');
    } catch (_) {
      toast.error('Health refresh failed');
    } finally { setBusy(false); }
  };

  if (!data) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const tel = Object.fromEntries((telemetry || []).map((t) => [t.model, t]));

  return (
    <div className="space-y-4" data-testid="admin-ai-models">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-400">Default model for members: <span className="font-mono text-slate-200">{data.default}</span> · last probe {data.checked_at ? new Date(data.checked_at).toLocaleString() : 'none since restart'}. Routing is unchanged; this view only reports health.</p>
        <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="model-health-refresh"><RefreshCw className={`h-4 w-4 mr-2 ${busy ? 'animate-spin' : ''}`} /> Re-probe</Button>
      </div>
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl divide-y divide-slate-700/30">
        {data.models.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5" data-testid={`model-row-${m.id}`}>
            <div className="min-w-[200px]">
              <p className="text-sm text-white">{m.label}</p>
              <p className="text-[11px] text-slate-500 font-mono">{m.id} · {m.provider}{!m.enabled && ' · disabled'}</p>
            </div>
            <Status m={m} />
            {tel[m.id] && <span className="text-[11px] text-slate-400 ml-auto">{tel[m.id].requests} requests · {Math.round(tel[m.id].success_rate * 100)}% success{tel[m.id].avg_latency_ms ? ` · ${tel[m.id].avg_latency_ms} ms avg` : ''}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminAIModels;
